import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useProfiles } from '@/hooks/useProfile';
import { useBlockedUserIds } from '@/hooks/useBlocks';
import type { Database } from '@/integrations/supabase/types';

type Profile = Database['public']['Tables']['profiles']['Row'];

export type ActiveMember = Profile & {
  isRecentlyActive: boolean;
};

export function useActiveMembers(cityCohort?: string) {
  const { user } = useAuth();
  const { data: profiles, isLoading: profilesLoading } = useProfiles({ cityCohort });
  const blockedIds = useBlockedUserIds();

  const { data: recentPosters, isLoading: postersLoading } = useQuery({
    queryKey: ['recent-posters'],
    queryFn: async () => {
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

      const { data, error } = await supabase
        .from('posts')
        .select('user_id, created_at')
        .eq('is_deleted', false)
        .gte('created_at', oneWeekAgo.toISOString())
        .order('created_at', { ascending: false })
        .limit(500);

      if (error) throw error;

      // Build a map of user_id -> most recent post date
      const userLastPost = new Map<string, string>();
      for (const row of data || []) {
        if (!userLastPost.has(row.user_id)) {
          userLastPost.set(row.user_id, row.created_at);
        }
      }
      return userLastPost;
    },
    staleTime: 1000 * 60 * 2,
  });

  const isLoading = profilesLoading || postersLoading;

  const members: ActiveMember[] = (() => {
    if (!profiles) return [];

    const activeUserIds = recentPosters || new Map<string, string>();

    const filtered = profiles.filter(
      (p) => p.user_id !== user?.id && !blockedIds.has(p.user_id)
    );

    const active: (Profile & { lastPostAt: string })[] = [];
    const inactive: Profile[] = [];

    for (const profile of filtered) {
      const lastPost = activeUserIds.get(profile.user_id);
      if (lastPost) {
        active.push({ ...profile, lastPostAt: lastPost });
      } else {
        inactive.push(profile);
      }
    }

    // Sort active by most recent post first
    active.sort((a, b) => b.lastPostAt.localeCompare(a.lastPostAt));

    // Sort inactive alphabetically
    inactive.sort((a, b) => a.full_name.localeCompare(b.full_name));

    return [
      ...active.map(({ lastPostAt, ...p }) => ({ ...p, isRecentlyActive: true })),
      ...inactive.map((p) => ({ ...p, isRecentlyActive: false })),
    ];
  })();

  return { members, isLoading };
}
